import { config } from '@/config/env';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Application logger
 * Only logs debug messages in development mode
 */
class Logger {
  private log(level: LogLevel, message: string, ...args: unknown[]) {
    // Skip debug logs in production
    if (!config.isDevelopment && level === 'debug') {
      return;
    }

    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    switch (level) {
      case 'debug':
        console.debug(prefix, message, ...args);
        break;
      case 'info':
        console.info(prefix, message, ...args);
        break;
      case 'warn':
        console.warn(prefix, message, ...args);
        break;
      case 'error':
        console.error(prefix, message, ...args);
        break;
    }
  }

  /**
   * Log debug message (development only)
   */
  debug(message: string, ...args: unknown[]) {
    this.log('debug', message, ...args);
  }

  /**
   * Log info message
   */
  info(message: string, ...args: unknown[]) {
    this.log('info', message, ...args);
  }

  /**
   * Log warning message
   */
  warn(message: string, ...args: unknown[]) {
    this.log('warn', message, ...args);
  }

  /**
   * Log error message
   */
  error(message: string, ...args: unknown[]) {
    this.log('error', message, ...args);
  }

  /**
   * Log API request
   */
  apiRequest(method: string, url: string, data?: unknown) {
    this.debug(`API Request: ${method} ${url}`, data);
  }

  /**
   * Log API response
   */
  apiResponse(method: string, url: string, status: number, data?: unknown) {
    this.debug(`API Response: ${method} ${url} - ${status}`, data);
  }

  /**
   * Log WebSocket message
   */
  wsMessage(event: string, data?: unknown) {
    this.debug(`WebSocket: ${event}`, data);
  }
}

export const logger = new Logger();
